/**
 * DataGrid Adapter for Facilities
 * Transforms field registry to MUI DataGrid column definitions
 */

import { GridColDef } from '@mui/x-data-grid';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { createCellRenderer as createSharedCellRenderer } from '../../../shared/utils/cellRenderers';
import {
  getGridFilterOperatorsForType,
  getAsyncSearchFilterOperators,
} from '../../../utils/fieldOperators';
import { FACILITIES_FIELDS_REGISTRY } from '../fields';
import { Facility } from '../../../services/api/domains/facilities/types';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Create a cell renderer for a specific field
 * Uses shared renderer with custom overrides for facility-specific fields
 */
function createFacilityCellRenderer(
  field: BaseFieldDefinition<Facility>
): GridColDef['renderCell'] {
  // Use shared renderer for all fields
  return createSharedCellRenderer<Facility>(field);
}

/**
 * Transform field registry to DataGrid column definitions
 */
export function toDataGridColumns(): GridColDef<Facility>[] {
  return FACILITIES_FIELDS_REGISTRY.filter(
    // Exclude internal IDs and counts from default display
    field =>
      field.key !== 'facility_id' &&
      field.key !== 'manufacturer_id' &&
      field.key !== 'manufacturer_facility_count'
  ).map(field => {
    const column: GridColDef<Facility> = {
      field: field.key,
      headerName: field.label,
      sortable: field.isSortable,
      filterable: field.isFilterable,
      editable: false, // Editing handled by modal, not inline
    };

    // Apply render configuration
    if (field.renderConfig) {
      if (field.renderConfig.width) column.width = field.renderConfig.width;
      if (field.renderConfig.minWidth)
        column.minWidth = field.renderConfig.minWidth;
      if (field.renderConfig.flex) column.flex = field.renderConfig.flex;
    }

    // Apply filter operators based on data type
    if (field.isFilterable) {
      // Special case: manufacturer_name should use manufacturer_id's relationConfig
      if (field.key === 'manufacturer_name') {
        const manufacturerIdField = FACILITIES_FIELDS_REGISTRY.find(
          f => f.key === 'manufacturer_id'
        );
        if (
          manufacturerIdField?.isRelation &&
          manufacturerIdField.relationConfig?.apiEndpoint &&
          manufacturerIdField.relationConfig?.searchField
        ) {
          const contextConfig = getContextAwareConfigFromRegistry(
            FACILITIES_FIELDS_REGISTRY,
            'manufacturer_id',
            'filter'
          );

          if (contextConfig) {
            const asyncConfig = toStandardAsyncSelectConfig(contextConfig);
            column.filterOperators = getAsyncSearchFilterOperators(
              asyncConfig.apiEndpoint,
              asyncConfig.searchField,
              asyncConfig.valueField,
              asyncConfig.labelField
            );
          }
        }
      }
      // Check for relational fields with async select
      else if (
        field.isRelation &&
        field.relationConfig?.apiEndpoint &&
        field.relationConfig?.searchField
      ) {
        // Get context-aware config for filters (uses name field)
        const contextConfig = getContextAwareConfigFromRegistry(
          FACILITIES_FIELDS_REGISTRY,
          field.key,
          'filter'
        );

        if (contextConfig) {
          const asyncConfig = toStandardAsyncSelectConfig(contextConfig);
          column.filterOperators = getAsyncSearchFilterOperators(
            asyncConfig.apiEndpoint,
            asyncConfig.searchField,
            asyncConfig.valueField,
            asyncConfig.labelField
          );
        } else {
          // Fallback to standard operators
          column.filterOperators = getGridFilterOperatorsForType(
            field.dataType as 'string' | 'number' | 'date'
          );
        }
      }
      // Check for selection fields
      else if (field.selectionOptions && field.selectionOptions.length > 0) {
        column.filterOperators = getGridFilterOperatorsForType(
          'selection',
          field.selectionOptions
        );
      } else {
        column.filterOperators = getGridFilterOperatorsForType(
          field.dataType as 'string' | 'number' | 'date'
        );
      }
    }

    // Apply custom cell renderer
    column.renderCell = createFacilityCellRenderer(field);

    // Add tooltip if description exists
    if (field.description) {
      column.description = field.description;
    }

    return column;
  });
}

/**
 * Get default column visibility model
 * Hides less frequently used columns by default
 */
export function getDefaultColumnVisibility(): Record<string, boolean> {
  return {
    address_line2: false,
    state_province: false,
    latitude: false,
    longitude: false,
    audit_date: false,
    audit_type: false,
    is_primary: false,
    comments: false,
    user_id: false,
    created_date: false,
    modified_date: false,
  };
}
